'use client';

import { c as _c } from "react/compiler-runtime";
import { ContentBrowser } from '@/components/content/ContentBrowser';
import type { ContentBrowserProps } from '@/components/content/ContentBrowser';
import type { ContentQuery } from '@/lib/api/types';
import { ContentListItem } from './ContentListItem';
export interface RelatedContentProps extends Omit<ContentBrowserProps, 'query' | 'minHeight' | 'scrollOnChange'> {
  /** The type of the parent object (e.g. 'initiative', 'entity', 'item'). */
  objectType: string;
  /** The ID of the parent object. */
  objectId: number | string;
  /** Tags to filter content by. */
  tags?: string[];
  /** Tag expression for complex filtering (e.g. { any: [...] }). */
  tagExpression?: Record<string, any>;
  /** Content types to include (defaults to news, article, announcement). */
  contentTypes?: string | string[];
  /** Max items to show. Defaults to 6. */
  limit?: number;
  /** Optional title rendered above the content browser. */
  title?: string;
  /** Additional query params merged into the content query. */
  query?: Partial<ContentQuery>;
}

/**
 * A reusable Related Content section that wraps ContentBrowser.
 * Designed to be embedded in object detail views (initiatives, entities, items, etc.)
 * to surface contextually relevant content based on tags.
 */
export function RelatedContent(t0) {
  const $ = _c(46);
  let browserProps;
  let extraQuery;
  let t1;
  let t10;
  let t11;
  let t12;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let tagExpression;
  let tags;
  if ($[0] !== t0) {
    const {
      objectType,
      objectId,
      tags: t13,
      tagExpression: t14,
      contentTypes: t15,
      limit: t16,
      title: t17,
      query: t18,
      searchEnabled: t19,
      showFilters: t20,
      showResultCount: t21,
      showViewModeToggle: t22,
      hideHeaderOnEmpty: t23,
      emptyState: t24,
      cardProps: t25,
      filterLayout: t26,
      viewMode: t27,
      ...t28
    } = t0;
    tags = t13;
    tagExpression = t14;
    t1 = t15;
    t2 = t16;
    t3 = t17;
    extraQuery = t18;
    t4 = t19;
    t5 = t20;
    t6 = t21;
    t7 = t22;
    t8 = t23;
    t9 = t24;
    t10 = t25;
    t11 = t26;
    t12 = t27;
    browserProps = t28;
    $[0] = t0;
    $[1] = browserProps;
    $[2] = extraQuery;
    $[3] = t1;
    $[4] = t10;
    $[5] = t11;
    $[6] = t12;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
    $[15] = tagExpression;
    $[16] = tags;
  } else {
    browserProps = $[1];
    extraQuery = $[2];
    t1 = $[3];
    t10 = $[4];
    t11 = $[5];
    t12 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
    t8 = $[13];
    t9 = $[14];
    tagExpression = $[15];
    tags = $[16];
  }
  let t13;
  if ($[17] !== t1) {
    t13 = t1 === undefined ? ["news", "article", "announcement"] : t1;
    $[17] = t1;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const contentTypes = t13;
  const limit = t2 === undefined ? 6 : t2;
  const title = t3 === undefined ? "Related Content" : t3;
  const searchEnabled = t4 === undefined ? false : t4;
  const showFilters = t5 === undefined ? false : t5;
  const showResultCount = t6 === undefined ? false : t6;
  const showViewModeToggle = t7 === undefined ? false : t7;
  const hideHeaderOnEmpty = t8 === undefined ? true : t8;
  let t14;
  if ($[19] !== t9) {
    t14 = t9 === undefined ? {
      title: "No related content",
      description: "",
      compact: true
    } : t9;
    $[19] = t9;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  const emptyState = t14;
  let t15;
  if ($[21] !== t10) {
    t15 = t10 === undefined ? {
      variant: "compact",
      showTypeBadge: true
    } : t10;
    $[21] = t10;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  const cardProps = t15;
  const filterLayout = t11 === undefined ? "mobile" : t11;
  const viewMode = t12 === undefined ? "list" : t12;
  let t16;
  if ($[23] !== contentTypes || $[24] !== extraQuery || $[25] !== tagExpression || $[26] !== tags) {
    t16 = {
      status: "published",
      visibility: "public",
      tags,
      tagExpression,
      type: contentTypes,
      ...extraQuery
    };
    $[23] = contentTypes;
    $[24] = extraQuery;
    $[25] = tagExpression;
    $[26] = tags;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const contentQuery = t16;
  let t17;
  if ($[28] !== title) {
    t17 = title && <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">{title}</h3>;
    $[28] = title;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== browserProps || $[31] !== cardProps || $[32] !== contentQuery || $[33] !== emptyState || $[34] !== filterLayout || $[35] !== hideHeaderOnEmpty || $[36] !== limit || $[37] !== searchEnabled || $[38] !== showFilters || $[39] !== showResultCount || $[40] !== showViewModeToggle || $[41] !== viewMode) {
    t18 = <ContentBrowser query={contentQuery} limit={limit} searchEnabled={searchEnabled} showFilters={showFilters} showResultCount={showResultCount} showViewModeToggle={showViewModeToggle} hideHeaderOnEmpty={hideHeaderOnEmpty} emptyState={emptyState} cardProps={cardProps} filterLayout={filterLayout} viewMode={viewMode} minHeight="none" scrollOnChange={false} {...browserProps} renderListItem={_temp} />;
    $[30] = browserProps;
    $[31] = cardProps;
    $[32] = contentQuery;
    $[33] = emptyState;
    $[34] = filterLayout;
    $[35] = hideHeaderOnEmpty;
    $[36] = limit;
    $[37] = searchEnabled;
    $[38] = showFilters;
    $[39] = showResultCount;
    $[40] = showViewModeToggle;
    $[41] = viewMode;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t17 || $[44] !== t18) {
    t19 = <section className="space-y-3">{t17}{t18}</section>;
    $[43] = t17;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  return t19;
}
function _temp(content) {
  return <ContentListItem content={content} />;
}